import type { Capability, GrantableScope, TeamMember } from '@/api/pending'

/** Final pt-BR copy (handoff §8, plus the Permissões screen added by the plan). */
export const SCOPE_LABEL: Record<GrantableScope, string> = {
  admin: 'Administrador',
  reviewer: 'Revisador',
  doorman: 'Porteiro',
}

export const SCOPE_DESCRIPTION: Record<GrantableScope, string> = {
  admin: 'Tudo neste painel, incluindo balancetes e permissões.',
  reviewer: 'Aprovar publicações, enquetes e reservas. Sem acesso financeiro.',
  doorman: 'Registrar encomendas e confirmar entrada de visitantes na portaria.',
}

export const SCOPE_POWERS: Record<GrantableScope, string[]> = {
  admin: [
    'Publicar e moderar tudo',
    'Publicar balancetes e ver dados financeiros',
    'Conceder e revogar acesso de outras pessoas',
  ],
  reviewer: [
    'Aprovar ou recusar publicações e enquetes',
    'Confirmar ou recusar reservas',
    'Sem acesso a finanças ou permissões',
  ],
  doorman: [
    'Registrar encomendas e visitantes na portaria',
    'Confirmar entregas e entradas',
    'Sem acesso a moderação ou finanças',
  ],
}

export const S = {
  subtitle: 'Os escopos definem quem pode publicar, moderar e confirmar reservas neste condomínio.',
  grant: 'Conceder acesso',
  permissions: 'Permissões',
  revoke: 'Revogar',

  grantTitle: 'Conceder acesso',
  email: 'E-mail',
  emailPlaceholder: 'nome@example.com',
  emailHint: 'A pessoa já precisa ter uma conta Condi.',
  scope: 'Escopo',
  reviewAndConfirm: 'Revisar e confirmar',
  cancel: 'Cancelar',
  back: 'Voltar',
  confirmTitle: 'Conceder este acesso?',
  willReceive: (scope: string) => `Vai receber o escopo ${scope} neste condomínio`,
  canDo: 'O que a pessoa poderá fazer',
  adminCaution:
    'Administrador é o escopo mais amplo: balancetes, permissões e todas as ações de moderação. Conceda só a alguém da equipe do síndico.',
  ack: 'Confirmei este endereço com a pessoa e ele é dela.',
  confirmGrant: 'Conceder escopo',
  granted: 'Acesso concedido',
  grantNotFound: 'Não encontramos uma conta Condi com este e-mail.',
  grantError: 'Não foi possível conceder o acesso agora. Tente de novo.',

  revokeTitle: 'Revogar este acesso?',
  revokeCaution: (scope: string) =>
    `A pessoa perde o escopo ${scope} na hora e é desconectada do painel. O que já foi publicado continua no feed. Você pode conceder o escopo de novo depois.`,
  revokeSelf: 'Você não pode revogar o seu próprio acesso. Peça a outro administrador.',
  revokeLastAdmin:
    'Este é o último administrador do condomínio. Conceda o escopo a outra pessoa antes de revogar.',
  typePrefix: 'Digite',
  typeSuffix: 'para confirmar',
  keep: 'Manter acesso',
  confirmRevoke: 'Revogar acesso',
  revoked: (name: string) => `${name} não tem mais acesso`,
  revokeError: 'Não foi possível revogar o acesso agora. Tente de novo.',

  permsTitle: 'Permissões',
  permsSubtitle:
    'Escolha o que cada escopo pode fazer neste condomínio, além do que já faz por padrão.',
  permsBack: 'Voltar para Equipe',
  permsLive: 'As mudanças valem na hora para toda a equipe deste condomínio.',
  permsLocked: 'Padrão do escopo',
  permsSave: 'Salvar permissões',
  permsSaved: 'Permissões atualizadas',
  permsError: 'Não foi possível salvar as permissões. Tente de novo.',
}

/** Capabilities an admin can add to Revisador/Porteiro. `overview`, `balances` and `team` never are. */
export const GRANTABLE_LABEL: Partial<Record<Capability, string>> = {
  moderation: 'Aprovações',
  bookings_review: 'Reservas',
  desk: 'Portaria',
  residents: 'Moradores e unidades',
  condo_info: 'Condomínio',
}

export const CAPABILITY_HINT: Partial<Record<Capability, string>> = {
  moderation: 'Aprovar ou recusar publicações e enquetes e moderar denúncias.',
  bookings_review: 'Confirmar ou recusar reservas das áreas comuns.',
  desk: 'Registrar encomendas e visitantes e entregar com o código.',
  residents: 'Buscar moradores e vincular ou remover pessoas das unidades.',
  condo_info: 'Editar e-mail, site, história, regras e contatos do condomínio.',
}

/** What each configurable scope can always do and can't be switched off. */
export const SCOPE_DEFAULT: Record<'reviewer' | 'doorman', Capability> = {
  reviewer: 'moderation',
  doorman: 'desk',
}

/** Client mirror of the server rule (plan API-6): not yourself, not the last admin. */
export function revokeBlock(
  member: TeamMember,
  team: TeamMember[],
  myUserId: number | undefined,
): 'self' | 'last_admin' | null {
  if (member.user_id === myUserId) return 'self'
  if (member.scope === 'admin' && team.filter((m) => m.scope === 'admin').length <= 1) {
    return 'last_admin'
  }
  return null
}
